import { Typography } from 'antd';
import { AIIcon, NeutralIcon, RightIcon } from 'assets';
import Avatar from 'components/common/avatar/Avatar';
import { useAIAgentCenterContext } from 'contexts/ai-agent-center';
import dayjs from 'dayjs';
import { aiAgentCenterMessages } from 'messages';
import { customerLookupMessages } from 'messages/customer-lookup.message';
import React, { memo } from 'react';
import { ChatSessionDto, ContactDto, ItemOption } from 'types';
import { AIContactEvaluationOption } from 'utils';
import ChatSessionTickets from './ChatSessionTickets';
import { useContactDetailByChat, useContactDetailByPhone } from 'hooks/contacts';
import CustomerLookupContactModal from 'components/customer-lookup-contact-histories/CustomerLookupContactModal';
import { useNavigate } from 'react-router-dom';
import { ROUTE } from 'routes/constants';
import { customizeEmpty } from 'components/common';
import { twMerge } from 'tailwind-merge';

const AIAgentContactInfo: React.FC = () => {
  const { callSession, type, chatSession } = useAIAgentCenterContext();
  const { contact: contactDetailByPhone } = useContactDetailByPhone(callSession?.customerPhoneNumber || '');
  const { contact: contactDetailByChat } = useContactDetailByChat(chatSession?.chatId);

  const contactDetail: ContactDto | undefined = type === 'phone' ? contactDetailByPhone : contactDetailByChat;

  return (
    <div className='h-[calc(100vh-178px)]'>
      <div>
        <div className='flex h-16 items-center border-b px-5'>
          <Typography.Text className='text-lg font-semibold'>
            {type === 'phone' ? aiAgentCenterMessages.callInfo : aiAgentCenterMessages.chatInfo}
          </Typography.Text>
        </div>
      </div>
      <div className='max-h-[calc(100%-64px)] overflow-y-auto py-5'>
        {contactDetail ? (
          <ContactInformation contact={{ ...contactDetail }} />
        ) : (
          <div className='border-b px-5 pb-6'>
            {customizeEmpty({
              title: aiAgentCenterMessages.noContactInfo,
              description: '  '
            })}
          </div>
        )}
        <AgentSupported
          isAI={chatSession?.isHandleByAi || callSession?.isHandleByAi}
          assigner={chatSession?.assigner || callSession?.assigner}
        />
        <ProcessingTime
          startAt={chatSession?.processingAt || callSession?.receivedAt}
          endAt={chatSession?.completedAt || callSession?.endAt}
        />
        <ChatSessionTickets chatSessionId={chatSession?.chatSessionId} callId={callSession?.callId} />
      </div>
    </div>
  );
};

const ContactInformation: React.FC<{ contact: ContactDto }> = ({ contact }) => {
  const navigate = useNavigate();

  const [open, setOpen] = React.useState(false);

  const optionEvaluation = contact?.contactSentiment?.sentiment
    ? AIContactEvaluationOption[contact.contactSentiment?.sentiment as keyof typeof AIContactEvaluationOption]
    : null;

  console.log(contact.contactSentiment, optionEvaluation);

  const personalInfoItems: ItemOption[] = [
    {
      label: customerLookupMessages.phoneNumber,
      value: contact.phoneNumber
    },
    {
      label: customerLookupMessages.idNumber,
      value: contact.idNumber
    },
    {
      label: customerLookupMessages.dateProvided,
      value: contact.idIssuedDate ? dayjs(contact.idIssuedDate).format('DD/MM/YYYY') : ''
    },
    {
      label: customerLookupMessages.placeProvided,
      value: contact.idIssuedPlace
    }
  ];

  return (
    <div className='border-b px-5 pb-6'>
      <Typography.Text className='font-semibold'>{aiAgentCenterMessages.contactInfo}</Typography.Text>
      <div className='mt-5 flex w-full flex-col items-center justify-center'>
        <div>
          <div className='relative h-20 w-20 rounded-full'>
            <Avatar size={80} name={contact.name} src={contact.avatarUrl} />
            <div className='absolute bottom-[1.5px] right-[1.5px] h-5 w-5'>
              <div
                className='absolute left-0.5 top-0.5 z-0 h-[17px] w-[17px] rounded-full'
                style={{
                  background: optionEvaluation ? optionEvaluation?.color : 'gray'
                }}
              />
              {optionEvaluation ? (
                optionEvaluation.icon(21, `text-white absolute left-0 top-0 `)
              ) : (
                <NeutralIcon className='absolute left-0 top-0 text-white' height={21} width={21} />
              )}
            </div>
          </div>
        </div>
        <div className='mb-2 mt-4 items-center justify-center text-center'>
          <Typography.Text className='text-lg font-semibold'>{contact.name}</Typography.Text>
        </div>
        <span
          className='rounded-base px-1.5 text-xs font-semibold uppercase leading-5 text-white'
          style={{
            background: optionEvaluation?.color
          }}
        >
          {optionEvaluation?.label}
        </span>
      </div>
      <div className='mt-6 flex flex-col gap-3'>
        {personalInfoItems.map((item, index) => (
          <div key={`personal-info-${index}`} className='flex gap-4'>
            <div className='w-[88px]'>
              <Typography.Text className='text-subTextColor'>{item.label}:</Typography.Text>
            </div>
            <div className='flex-1'>
              <Typography.Text>{item.value || '-'}</Typography.Text>
            </div>
          </div>
        ))}
      </div>

      <div className='mt-6'>
        <Typography.Link
          onClick={() => {
            setOpen(true);
          }}
          className='flex items-center gap-2'
        >
          {aiAgentCenterMessages.contactProfile} <RightIcon height={16} width={16} />
        </Typography.Link>
      </div>
      <CustomerLookupContactModal
        searchCriteria={{}}
        open={open}
        onClose={() => setOpen(false)}
        contactId={contact.contactId}
        handleCustomerCodeClick={(code) => {
          navigate(ROUTE.CUSTOMER_LOOKUP, {
            state: {
              customerCode: code
            }
          });
        }}
      />
    </div>
  );
};

const AgentSupported: React.FC<{ isAI?: boolean; assigner: ChatSessionDto['assigner'] }> = ({ isAI, assigner }) => {
  return (
    <div className='border-b px-5 pb-6 pt-5'>
      <Typography.Text className='font-semibold'>{aiAgentCenterMessages.agentSupported}</Typography.Text>
      <div className={twMerge('mt-5 flex gap-4', isAI ? 'items-center' : 'items-start')}>
        <div>
          {isAI ? (
            <AIIcon height={48} width={48} />
          ) : (
            <Avatar size={48} fileId={assigner?.avatar} name={assigner?.fullName} />
          )}
        </div>
        <div className={!isAI ? 'mt-[3px]' : ''}>
          <Typography.Text>{isAI ? 'AI' : assigner?.fullName}</Typography.Text>
          {!isAI && (
            <Typography.Paragraph type='secondary' className='mb-0 mt-1 text-sm'>
              Ext.{assigner?.extensionId}
            </Typography.Paragraph>
          )}
        </div>
      </div>
    </div>
  );
};

const ProcessingTime: React.FC<{ startAt?: string; endAt?: string }> = ({ startAt, endAt }) => {
  return (
    <div className='border-b px-5 pb-6 pt-5'>
      <Typography.Text className='font-semibold'>{aiAgentCenterMessages.processingTime}</Typography.Text>
      <div className='mt-4'>
        <Typography.Text>
          Từ {dayjs(startAt).format('HH:mm')} {endAt && `đến ${dayjs(endAt).format('HH:mm')}`}
        </Typography.Text>
      </div>
    </div>
  );
};

export default memo(AIAgentContactInfo);
